import { useDispatch, useSelector } from 'react-redux'
import { Typography } from '@mui/material'

import styles from '../../components/UserDashboard/User/index.module.scss'
import ButtonCustom from '../../components/atoms/button/ButtonCustom'
import { WeddingTab } from '../../components/UserDashboard/User/WeddingTab'
import OnBoardingKazoo from '../onboarding/Kazoo'
import { useCallback, useEffect, useState, memo } from 'react'
import WelCome from '../onboarding/NewWeddingModal/WelCome'
import WeddingName from '../onboarding/NewWeddingModal/WeddingName'
import WeddingDate from '../onboarding/NewWeddingModal/WeddingDate'
import Planning from '../onboarding/NewWeddingModal/Planning'
import Budget from '../onboarding/NewWeddingModal/Budget'
import Guests from '../onboarding/NewWeddingModal/Guests'
import GuestsTravel from '../onboarding/NewWeddingModal/GuestsTravel'
import ColorScheme from '../onboarding/NewWeddingModal/ColorScheme'
import WeddingTheme from '../onboarding/NewWeddingModal/WeddingTheme'
import CeremonyVenue from '../onboarding/NewWeddingModal/CeremonyVenue'
import ReceptionVenue from '../onboarding/NewWeddingModal/ReceptionVenue'
import VenueAddress from '../onboarding/NewWeddingModal/VenueAddress'
import Dinner from '../onboarding/NewWeddingModal/Dinner'
import Cultural from '../onboarding/NewWeddingModal/Cultural'
import CulturalSignificances from '../onboarding/NewWeddingModal/CulturalSignificances'
import LiveMusic from '../onboarding/NewWeddingModal/LiveMusic'
import Food from '../onboarding/NewWeddingModal/Food'
import ShareInvitations from '../onboarding/NewWeddingModal/ShareInvitations'
import CeremonyVibe from '../onboarding/NewWeddingModal/CeremonyVibe'
import ReceptionVibe from '../onboarding/NewWeddingModal/ReceptionVibe'
import { getMyKazoos, getOtherKazoos } from '../../features/kazooEventSlice'
import CaptureYourDay from '../onboarding/NewWeddingModal/CaptureYourDay'
import PlanningSupport from '../onboarding/NewWeddingModal/PlanningSupport'
import WeddingPlanner from '../onboarding/NewWeddingModal/WeddingPlanner'
import { useTutorial } from '../../context/TutorialContext'
import { useTutorialStatus } from '../../hooks/useTutorialStatus'
import { getWeddingOnboardingInitialState } from '../../constants/weddingOnboarding'

const LAST_WIZARD_STEP = 23

const NewWeddingWizard = memo(function NewWeddingWizard({ open, onClose }) {
	const [weddingData, setWeddingData] = useState(
		getWeddingOnboardingInitialState
	)
	const [step, setStep] = useState(0)

	const handleClose = useCallback(() => {
		setWeddingData(getWeddingOnboardingInitialState())
		setStep(0)
		onClose()
	}, [onClose])

	const handleBack = useCallback((s) => {
		const delta = typeof s === 'number' ? s : 1
		setStep((prev) => Math.max(prev - delta, 0))
	}, [])

	const handleNext = useCallback(
		(s) => {
			setStep((prev) => {
				if (prev === LAST_WIZARD_STEP) {
					setWeddingData(getWeddingOnboardingInitialState())
					onClose()
					return 0
				}
				const delta = typeof s === 'number' ? s : 1
				return prev + delta
			})
		},
		[onClose]
	)

	const steps = [
		<WelCome
			key="welcome"
			handleNext={handleNext}
			open={open && step === 0}
			handleClose={handleClose}
			weddingData={weddingData}
			setWeddingData={setWeddingData}
		/>,
		<WeddingName
			key="name"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 1}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<WeddingDate
			key="date"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 2}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<Planning
			key="planning"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 3}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<WeddingPlanner
			key="planner"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 4}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<PlanningSupport
			key="support"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 5}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<Budget
			key="budget"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 6}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<Guests
			key="guests"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 7}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<GuestsTravel
			key="travel"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 8}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<ColorScheme
			key="color"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 9}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<WeddingTheme
			key="theme"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 10}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<CeremonyVenue
			key="ceremony-venue"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 11}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<VenueAddress
			key="ceremony-address"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 12}
			handleClose={handleClose}
			handleBack={handleBack}
			type="Ceremony"
		/>,
		<CeremonyVibe
			key="ceremony-vibe"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 13}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<ReceptionVenue
			key="reception-venue"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 14}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<VenueAddress
			key="reception-address"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 15}
			handleClose={handleClose}
			handleBack={handleBack}
			type="Reception"
		/>,
		<ReceptionVibe
			key="reception-vibe"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 16}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<Dinner
			key="dinner"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 17}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<Food
			key="food"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 18}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<Cultural
			key="cultural"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 19}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<CulturalSignificances
			key="cultural-sig"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 20}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<CaptureYourDay
			key="capture"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 21}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<LiveMusic
			key="music"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 22}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
		<ShareInvitations
			key="share"
			weddingData={weddingData}
			setWeddingData={setWeddingData}
			handleNext={handleNext}
			open={open && step === 23}
			handleClose={handleClose}
			handleBack={handleBack}
		/>,
	]

	return steps[step] ?? null
})

export const UserDashboardPage = () => {
	const dispatch = useDispatch()

	const myKazoos = useSelector((state) => state.kazooEvent.myKazoos)
	const otherKazoos = useSelector((state) => state.kazooEvent.otherKazoos)

	const [modal, setModal] = useState(null)
	const isSidebarOpen = useSelector((state) => state.sidebar.isSidebarOpen)

	const openFilter = (e) => setFilterAnchor(e.currentTarget)

	const handleSelectModal = useCallback((index) => {
		setModal(index)
	}, [])

	const [open, setOpen] = useState(false)
	const closeWizard = useCallback(() => setOpen(false), [])

	const { startTutorial, isTutorialActive } = useTutorial()
	const { hasSeenTutorial, isLoading } = useTutorialStatus('new-wedding')
	useEffect(() => {
		if (
			!isLoading &&
			!hasSeenTutorial &&
			myKazoos &&
			!isTutorialActive() &&
			myKazoos.length === 3
		) {
			const timer = setTimeout(() => {
				startTutorial('new-wedding')
			}, 1000)
			return () => clearTimeout(timer)
		}
	}, [myKazoos, isLoading, hasSeenTutorial, isTutorialActive, startTutorial])

	useEffect(() => {
		dispatch(getMyKazoos())
		dispatch(getOtherKazoos())
	}, [dispatch])

	return (
		<>
			<div className={styles.pageHeader}>
				<Typography component="h1"> Dashboard</Typography>
				<div className={styles.right}>
					<ButtonCustom
						id="new-wedding-btn"
						data-cy="kazoo-button"
						variant="default"
						size="lg"
						onClick={() => setOpen(true)}
						disabled={myKazoos.length >= 1}
						title={
							myKazoos.length >= 1
								? 'You can only have one active wedding at a time'
								: ''
						}
					>
						+ New Wedding
					</ButtonCustom>
				</div>
			</div>
			<NewWeddingWizard open={open} onClose={closeWizard} />
			<div className={styles.pageMain}>
				<WeddingTab
					isSidebarOpen={isSidebarOpen}
					onClickFilter={openFilter}
					myKazoos={myKazoos}
					otherKazoos={otherKazoos}
				/>
				<OnBoardingKazoo
					index={modal}
					handleSetIndex={handleSelectModal}
					resetIndex={() => setModal(null)}
				/>
			</div>
		</>
	)
}
